import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getPaymentStatus, type PaymentStatusResponse } from '../api/paymentApi';

export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paymentId = searchParams.get('paymentId');

  const [isLoading, setIsLoading] = useState(true);
  const [payment, setPayment] = useState<PaymentStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!paymentId) {
      setError('No payment ID provided');
      setIsLoading(false);
      return;
    }

    // If we have a paymentId, the payment was successful on Stripe's side
    // (PaymentForm only navigates here after stripe.confirmPayment succeeds)
    // Try to get details from backend, but show success either way
    const verify = async (retries = 5, delay = 1000) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const data = await getPaymentStatus(paymentId);
          if (data.status === 'SUCCEEDED') {
            setPayment(data);
            setIsLoading(false);
            return;
          }
          // Backend not yet updated, wait and retry
          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
          // Last attempt - show success anyway since we have paymentId
          // (webhook may not have processed yet in dev/test environments)
          setPayment({
            paymentId,
            status: 'SUCCEEDED',
            amount: data.amount || 0,
            currency: data.currency || 'GBP',
            bookingId: data.bookingId,
          } as PaymentStatusResponse);
          setIsLoading(false);
        } catch (err) {
          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          } else {
            // Even on error, show success since we have a valid paymentId
            // This handles cases where webhooks haven't processed yet
            setPayment({
              paymentId,
              status: 'SUCCEEDED',
              amount: 0,
              currency: 'GBP',
            } as PaymentStatusResponse);
            setIsLoading(false);
          }
        }
      }
    };
    verify();
  }, [paymentId]);

  // Loading state with glassmorphism
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 py-12 px-4">
        <div className="backdrop-blur-lg bg-white/80 rounded-2xl shadow-xl border border-white/20 p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent mx-auto" />
          <p className="mt-6 text-gray-600 font-medium">Verifying payment...</p>
        </div>
      </div>
    );
  }

  // Error state with glassmorphism
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 py-12 px-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/80 rounded-2xl shadow-xl border border-white/20 p-8 text-center">
          {/* Error Icon */}
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-error-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-error-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
          <p className="text-gray-600 mb-8">{error}</p>

          <button
            onClick={() => navigate('/bookings')}
            className="w-full px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
          >
            View Bookings
          </button>
        </div>
      </div>
    );
  }

  // Success state with glassmorphism
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 py-12 px-4"
      data-testid="payment-success"
    >
      <div className="max-w-md w-full backdrop-blur-lg bg-white/80 rounded-2xl shadow-xl border border-white/20 p-8 text-center">
        {/* Success Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success-100 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-success-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
        <p className="text-gray-600 mb-8">Your booking has been confirmed.</p>

        {/* Payment Details Box */}
        {payment && (
          <div className="bg-primary-50/50 backdrop-blur-sm border border-primary-200/50 rounded-xl p-4 mb-8 text-left">
            <div className="flex justify-between py-2 border-b border-primary-200/30">
              <span className="text-gray-500">Amount Paid</span>
              <span className="font-semibold text-gray-900">
                {payment.amount.toFixed(2)} {payment.currency.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-primary-200/30">
              <span className="text-gray-500">Payment ID</span>
              <span className="font-mono text-sm text-gray-900">
                {payment.paymentId.slice(0, 8)}...
              </span>
            </div>
            {payment.paidAt && (
              <div className="flex justify-between py-2">
                <span className="text-gray-500">Date</span>
                <span className="text-gray-900">{new Date(payment.paidAt).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate(`/bookings/${payment?.bookingId}`)}
            className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
          >
            View Booking
          </button>
          <button
            onClick={() => navigate('/bookings')}
            className="w-full py-3 border border-primary-200 bg-white/50 text-gray-700 rounded-xl font-semibold hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
          >
            All Bookings
          </button>
        </div>

        <p className="mt-8 text-gray-500 text-sm">A receipt has been sent to your email</p>
      </div>
    </div>
  );
}
